import { useState, useEffect, useRef } from 'react';
import {
  MessageCircle,
  Search,
  Send,
  User,
  Shield,
  Bot,
  RefreshCcw,
  AlertCircle,
  Car,
  ExternalLink,
  FileCheck,
  Play,
  Terminal,
  CheckCircle2,
  X,
  Sparkles,
  Smartphone,
  Layers,
  ArrowRight
} from 'lucide-react';
import apiClient from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface WhatsAppChat {
  id: string;
  phoneNumber: string;
  customerName: string;
  currentState: string;
  isTakeover: boolean;
  unreadCount: number;
  lastInteraction: string;
}

interface WhatsAppMessage {
  id: string;
  sender: 'User' | 'Bot' | 'Agent';
  messageText: string;
  timestamp: string;
}

export default function WhatsAppChats() {
  const [chats, setChats] = useState<WhatsAppChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<WhatsAppChat | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Simulator State
  const [showSimModal, setShowSimModal] = useState(false);
  const [simTab, setSimTab] = useState<'quote' | 'fnol' | 'takeover' | 'payload'>('quote');
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState<any>(null);

  // Form parameters
  const [simPhone, setSimPhone] = useState('+265991823741');
  const [simName, setSimName] = useState('Chikondi Phiri');
  const [simReg, setSimReg] = useState('BW 4421');
  const [simMakeModel, setSimMakeModel] = useState('Toyota Hilux 2.8 GD-6');
  const [simValue, setSimValue] = useState(25000000);
  const [simCoverType, setSimCoverType] = useState('Comprehensive');
  const [simPaymentMethod, setSimPaymentMethod] = useState('Airtel Money');
  const [simPeril, setSimPeril] = useState('AccidentalCollision');
  const [simLossDesc, setSimLossDesc] = useState('Collision with minibus on blind corner. Radiator punctured, bumper damaged.');
  const [simPayloadType, setSimPayloadType] = useState<'text' | 'button' | 'list'>('text');
  const [simMessageText, setSimMessageText] = useState('motor quote');

  const handleRunSimulation = async () => {
    setSimLoading(true);
    setSimResult(null);
    try {
      if (simTab === 'quote') {
        const res = await apiClient.post('/admin/whatsapp/chats/simulate/quote', {
          phoneNumber: simPhone,
          customerName: simName,
          registrationNumber: simReg,
          makeModel: simMakeModel,
          vehicleValue: simValue,
          coverType: simCoverType,
          paymentMethod: simPaymentMethod
        });
        setSimResult({ type: 'quote', data: res.data });
        toast.success('Motor quote conversational simulation completed!');
      } else if (simTab === 'fnol') {
        const res = await apiClient.post('/admin/whatsapp/chats/simulate/fnol', {
          phoneNumber: simPhone,
          policyOrRegNumber: simReg,
          dateAndLocation: 'Yesterday 16:30 along M1 highway near Dedza',
          peril: simPeril,
          driverDetails: 'Tiwonge Banda (MW-DL-77218)',
          lossDescription: simLossDesc
        });
        setSimResult({ type: 'fnol', data: res.data });
        toast.success('Claims FNOL simulation completed!');
      } else if (simTab === 'takeover') {
        const res = await apiClient.post('/admin/whatsapp/chats/simulate/takeover', {
          phoneNumber: simPhone,
          agentName: 'Sarah Chirwa',
          agentMessage: 'Hello Chikondi, I am reviewing your COMESA Yellow Card extension now. Cross-border transit through Zambia is covered under endorsement YC-ZM.'
        });
        setSimResult({ type: 'takeover', data: res.data });
        toast.success('Live agent takeover simulation completed!');
      } else if (simTab === 'payload') {
        const res = await apiClient.post('/admin/whatsapp/chats/simulate/webhook-payload', {
          payloadType: simPayloadType,
          phoneNumber: simPhone,
          messageText: simMessageText,
          interactiveId: simPayloadType === 'button' ? 'btn_motor_quote' : 'list_motor_quote',
          interactiveTitle: simPayloadType === 'button' ? '🚗 Motor Quote' : 'Motor Vehicle Cover'
        });
        setSimResult({ type: 'payload', data: res.data });
        toast.success('Meta webhook payload generated & parsed!');
      }
      fetchChats(false);
    } catch (err: any) {
      toast.error('Simulation failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSimLoading(false);
    }
  };

  // Poll chats list every 5 seconds
  useEffect(() => {
    fetchChats(true);
    const interval = setInterval(() => {
      fetchChats(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Poll active conversation messages every 4 seconds if a chat is selected
  useEffect(() => {
    if (!selectedChat) return;
    fetchMessages(selectedChat.id, false);
    const interval = setInterval(() => {
      fetchMessages(selectedChat.id, false);
    }, 4000);
    return () => clearInterval(interval);
  }, [selectedChat]);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChats = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await apiClient.get<WhatsAppChat[]>('/admin/whatsapp/chats');
      setChats(res.data);
      // Update selected chat details if open
      if (selectedChat) {
        const updated = res.data.find(c => c.id === selectedChat.id);
        if (updated) setSelectedChat(updated);
      }
    } catch (err) {
      console.error('Failed to fetch WhatsApp sessions:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const fetchMessages = async (chatId: string, showSpinner = false) => {
    if (showSpinner) setLoadingMessages(true);
    try {
      const res = await apiClient.get<WhatsAppMessage[]>(`/admin/whatsapp/chats/${chatId}/messages`);
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to fetch conversation history:', err);
    } finally {
      if (showSpinner) setLoadingMessages(false);
    }
  };

  const handleSelectChat = (chat: WhatsAppChat) => {
    setSelectedChat(chat);
    fetchMessages(chat.id, true);
    // Mark as read in UI instantly
    if (chat.unreadCount > 0) {
      setChats(prev => prev.map(c => c.id === chat.id ? { ...c, unreadCount: 0 } : c));
    }
  };

  const handleToggleTakeover = async (chat: WhatsAppChat) => {
    const nextState = !chat.isTakeover;
    try {
      await apiClient.post(`/admin/whatsapp/chats/${chat.id}/takeover`, { isTakeover: nextState });
      toast.success(nextState ? 'AI Muted. You are now in control!' : 'Takeover ended. AI Bot activated.');
      fetchChats(false);
    } catch (err) {
      toast.error('Failed to toggle human takeover.');
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChat || !inputText.trim() || sending) return;

    setSending(true);
    try {
      await apiClient.post(`/admin/whatsapp/chats/${selectedChat.id}/send`, { messageText: inputText });
      setInputText('');
      fetchMessages(selectedChat.id, false);
      fetchChats(false);
    } catch (err) {
      toast.error('Failed to send WhatsApp message.');
    } finally {
      setSending(false);
    }
  };

  const filteredChats = chats.filter(c =>
    c.phoneNumber.includes(searchTerm) ||
    c.customerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto h-[calc(100vh-140px)] flex flex-col">
      {/* Top Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <MessageCircle className="text-teal-600 w-7 h-7" /> WhatsApp Support Center
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">Manage live conversations and hybrid AI takeover controls</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowSimModal(true); setSimResult(null); }}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
          >
            <Sparkles className="w-3.5 h-3.5" /> Webhook Simulator
          </button>
          <button
            onClick={() => fetchChats(true)}
            className="p-2 text-slate-500 dark:text-slate-450 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all border border-slate-200 dark:border-slate-800 bg-[#ffffff] hover:bg-[#ffffff] dark:bg-slate-900"
            title="Refresh Chats"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Splitscreen Container */}
      <div className="flex-1 bg-white rounded-3xl border border-slate-100 dark:border-slate-800 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden flex">
        {/* Left Pane - Chats list */}
        <div className="w-80 border-r border-slate-100 dark:border-slate-800 flex flex-col">
          {/* Search bar */}
          <div className="p-4 border-b border-slate-100 dark:border-slate-800 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-8 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search chat or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors text-slate-800 dark:text-slate-100"
            />
          </div>

          {/* Active List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-55 dark:divide-slate-850">
            {filteredChats.length === 0 ? (
              <div className="p-8 text-center text-slate-400 dark:text-slate-500 text-sm">
                No active conversations found
              </div>
            ) : (
              filteredChats.map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => handleSelectChat(chat)}
                  className={`w-full p-4 flex items-start gap-3 transition-colors text-left
                    ${selectedChat?.id === chat.id ? 'bg-slate-50 dark:bg-slate-800/40 border-l-4 border-teal-500' : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/20'}`}
                >
                  <div className={`p-2.5 rounded-xl shrink-0 ${chat.isTakeover ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-teal-50 dark:bg-teal-950/20'}`}>
                    {chat.isTakeover ? (
                      <User className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <Bot className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-slate-800 dark:text-slate-200 text-sm truncate">{chat.customerName}</span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500">
                        {new Date(chat.lastInteraction).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">+{chat.phoneNumber}</span>
                      <div className="flex items-center gap-1.5">
                        {chat.currentState.startsWith('MOTOR_') && (
                          <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Car className="w-2.5 h-2.5" /> Quoting
                          </span>
                        )}
                        {chat.unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
                            {chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Center Pane - Message History */}
        <div className="flex-1 flex flex-col bg-slate-50/50 dark:bg-slate-950/20">
          {selectedChat ? (
            <>
              {/* Timeline Header */}
              <div className="bg-white dark:bg-slate-900 p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800 dark:text-slate-200">{selectedChat.customerName}</h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      selectedChat.currentState.startsWith('MOTOR_')
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    }`}>
                      {selectedChat.currentState.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">+{selectedChat.phoneNumber}</span>
                </div>

                {/* Takeover Control Toggle */}
                <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-2 px-4 rounded-2xl">
                  <div className="flex items-center gap-2">
                    {selectedChat.isTakeover ? (
                      <Shield className="w-4 h-4 text-amber-500 shrink-0" />
                    ) : (
                      <Bot className="w-4 h-4 text-teal-500 shrink-0" />
                    )}
                    <span className="text-xs font-bold text-slate-600 dark:text-slate-350">
                      {selectedChat.isTakeover ? 'Human Control' : 'AI Bot Active'}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={selectedChat.isTakeover}
                      onChange={() => handleToggleTakeover(selectedChat)}
                    />
                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
              </div>

              {/* Message Feed Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingMessages ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-sm">
                    No message logs recorded yet.
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isUser = msg.sender === 'User';
                    const isBot = msg.sender === 'Bot';
                    const hasCoverNote = msg.messageText.includes('CoverNote') || msg.messageText.includes('download-cover-note');
                    const hasDisc = msg.messageText.includes('WindshieldDisc') || msg.messageText.includes('download-windshield-disc');
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3.5 rounded-2xl text-sm leading-relaxed shadow-sm
                            ${isUser 
                              ? 'bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-850 text-slate-800 dark:text-slate-200 rounded-tl-none' 
                              : isBot 
                                ? 'bg-slate-600 text-white rounded-tr-none' 
                                : 'bg-teal-600 text-white rounded-tr-none'}`}
                        >
                          <p className="whitespace-pre-line">{msg.messageText}</p>
                          {(hasCoverNote || hasDisc) && (
                            <div className="mt-2.5 pt-2 border-t border-white/20 flex flex-wrap gap-2">
                              {hasCoverNote && (
                                <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2 py-0.5 rounded font-medium">
                                  <FileCheck className="w-3 h-3" /> Official Cover Note PDF
                                </span>
                              )}
                              {hasDisc && (
                                <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2 py-0.5 rounded font-medium">
                                  <Car className="w-3 h-3" /> Digital Disc PDF
                                </span>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1 text-[9px] opacity-60">
                            <span className="font-semibold uppercase tracking-wider">
                              {msg.sender}
                            </span>
                            <span>
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Message Entry input */}
              <div className="bg-white p-4 border-t border-slate-100 dark:border-slate-800">
                {!selectedChat.isTakeover && (
                  <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/20 text-blue-800 dark:text-blue-400 rounded-xl border border-blue-100 dark:border-blue-900/30 flex items-center gap-2 text-xs font-semibold">
                    <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                    Toggle human takeover to mute the AI and reply directly to this customer.
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    disabled={!selectedChat.isTakeover || sending}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={selectedChat.isTakeover ? "Type WhatsApp response here..." : "Take over chat to respond"}
                    className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 outline-none text-sm focus:border-teal-500 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500"
                  />
                  <button
                    type="submit"
                    disabled={!selectedChat.isTakeover || !inputText.trim() || sending}
                    className="p-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500 transition-colors flex items-center justify-center shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 p-8">
              <MessageCircle className="w-16 h-16 mb-4 opacity-20" />
              <h3 className="font-bold text-slate-700 dark:text-slate-350 text-base mb-1">Select a Conversation</h3>
              <p className="text-sm text-center max-w-xs">Choose a chat session from the list on the left to view logs and toggle bot control.</p>
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Webhook & Bot Simulator Modal */}
      {showSimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-4xl w-full border border-slate-100 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-850">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300 rounded-2xl">
                  <Terminal className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">WhatsApp Webhook & Chatbot Simulator</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Test conversational rating, automated FNOL lodging, and Meta payloads</p>
                </div>
              </div>
              <button
                onClick={() => setShowSimModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Navigation Tabs */}
            <div className="flex border-b border-slate-100 dark:border-slate-800 px-6 bg-white dark:bg-slate-900 gap-2 pt-3">
              <button
                onClick={() => { setSimTab('quote'); setSimResult(null); }}
                className={`pb-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
                  simTab === 'quote'
                    ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-300'
                    : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <Car className="w-3.5 h-3.5" /> Motor Quoting SLA
              </button>
              <button
                onClick={() => { setSimTab('fnol'); setSimResult(null); }}
                className={`pb-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
                  simTab === 'fnol'
                    ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-300'
                    : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <AlertCircle className="w-3.5 h-3.5" /> Claims FNOL Intake
              </button>
              <button
                onClick={() => { setSimTab('takeover'); setSimResult(null); }}
                className={`pb-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
                  simTab === 'takeover'
                    ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-300'
                    : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <User className="w-3.5 h-3.5" /> Agent Takeover Escalation
              </button>
              <button
                onClick={() => { setSimTab('payload'); setSimResult(null); }}
                className={`pb-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 ${
                  simTab === 'payload'
                    ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-300'
                    : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" /> Raw Meta Payload
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Parameter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/60">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase">Customer Phone</label>
                  <input
                    type="text"
                    value={simPhone}
                    onChange={(e) => setSimPhone(e.target.value)}
                    className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-mono"
                  />
                </div>
                {simTab === 'quote' && (
                  <>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Vehicle Reg</label>
                      <input
                        type="text"
                        value={simReg}
                        onChange={(e) => setSimReg(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Sum Insured (MWK)</label>
                      <input
                        type="number"
                        value={simValue}
                        onChange={(e) => setSimValue(Number(e.target.value))}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Cover Type</label>
                      <select
                        value={simCoverType}
                        onChange={(e) => setSimCoverType(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      >
                        <option value="Comprehensive">Comprehensive</option>
                        <option value="ThirdPartyOnly">Third Party Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Payment Channel</label>
                      <select
                        value={simPaymentMethod}
                        onChange={(e) => setSimPaymentMethod(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      >
                        <option value="Airtel Money">Airtel Money</option>
                        <option value="TNM Mpamba">TNM Mpamba</option>
                        <option value="Credit Card">Credit Card</option>
                      </select>
                    </div>
                  </>
                )}
                {simTab === 'fnol' && (
                  <>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Peril Type</label>
                      <select
                        value={simPeril}
                        onChange={(e) => setSimPeril(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      >
                        <option value="AccidentalCollision">Accidental Collision</option>
                        <option value="TotalTheft">Total Theft</option>
                        <option value="FireDamage">Fire Damage</option>
                        <option value="WindscreenBreakage">Windscreen Breakage</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Vehicle / Policy</label>
                      <input
                        type="text"
                        value={simReg}
                        onChange={(e) => setSimReg(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      />
                    </div>
                  </>
                )}
                {simTab === 'payload' && (
                  <>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Payload Type</label>
                      <select
                        value={simPayloadType}
                        onChange={(e) => setSimPayloadType(e.target.value as any)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      >
                        <option value="text">Text Message (body: "motor quote")</option>
                        <option value="button">Interactive Button Reply (id: "btn_motor_quote")</option>
                        <option value="list">Interactive List Reply (id: "list_motor_quote")</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-500 uppercase">Message Text</label>
                      <input
                        type="text"
                        value={simMessageText}
                        onChange={(e) => setSimMessageText(e.target.value)}
                        className="w-full mt-1 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Action Trigger */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Simulates full asynchronous Meta Webhook delivery and bot state engine progression.
                </span>
                <button
                  onClick={handleRunSimulation}
                  disabled={simLoading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-md transition-all shrink-0"
                >
                  {simLoading ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" /> Simulating...
                    </>
                  ) : (
                    <>
                      <Play className="w-3.5 h-3.5" /> Execute Simulation
                    </>
                  )}
                </button>
              </div>

              {/* Simulation Result Console */}
              {simResult && (
                <div className="border border-slate-100 dark:border-slate-800 rounded-2xl bg-slate-950 text-slate-100 p-5 space-y-4 font-mono text-xs shadow-inner">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-teal-400 font-bold">
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      SIMULATION COMPLETED (100% SLA PASS)
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">Xtrem Simulator v2.0</span>
                  </div>

                  {simResult.data.steps && (
                    <div className="space-y-3">
                      <div className="text-[11px] text-slate-400 uppercase tracking-wider font-bold">Multi-Turn Conversation Transcript:</div>
                      {simResult.data.steps.map((st: any) => (
                        <div key={st.stepIndex} className="bg-slate-900/90 p-3 rounded-xl border border-slate-800/80 space-y-1">
                          <div className="flex items-center justify-between text-teal-300 font-semibold text-[11px]">
                            <span>Step {st.stepIndex}: {st.detectedState}</span>
                            {st.artifactGenerated && (
                              <span className="bg-emerald-950 text-emerald-300 px-2 py-0.5 rounded text-[10px]">
                                {st.artifactGenerated}
                              </span>
                            )}
                          </div>
                          <div className="text-slate-300">
                            <span className="text-amber-400 font-bold">User:</span> "{st.userMessage}"
                          </div>
                          <div className="text-slate-400 text-[11px] whitespace-pre-line pl-3 border-l-2 border-teal-500/40">
                            {st.botResponse}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bound Policy Summary */}
                  {simResult.data.boundPolicy && (
                    <div className="bg-emerald-950/30 border border-emerald-900/50 p-4 rounded-xl text-emerald-300 space-y-1 text-xs">
                      <div className="font-bold text-sm text-emerald-200">✓ Official Policy Bound In-Force</div>
                      <div>Policy Number: <span className="font-bold text-white">{simResult.data.boundPolicy.policyNumber}</span></div>
                      <div>Digital Cover Note: <span className="font-bold text-white">{simResult.data.boundPolicy.digitalCoverNoteNumber}</span></div>
                      <div>Anti-Counterfeit QR Token: <span className="font-bold text-white">{simResult.data.boundPolicy.verificationToken}</span></div>
                      <div>Gross Written Premium: <span className="font-bold text-white">MWK {simResult.data.boundPolicy.grossWrittenPremium?.toLocaleString()}</span></div>
                      <div>Dual Vector PDFs: <span className="text-emerald-400">Cover Note & Windshield Disc Generated</span></div>
                    </div>
                  )}

                  {/* Lodged Claim Summary */}
                  {simResult.data.lodgedClaim && (
                    <div className="bg-emerald-950/30 border border-emerald-900/50 p-4 rounded-xl text-emerald-300 space-y-1 text-xs">
                      <div className="font-bold text-sm text-emerald-200">✓ Claim Successfully Registered & OSLR Posted</div>
                      <div>Claim Reference: <span className="font-bold text-white">{simResult.data.lodgedClaim.claimNumber}</span></div>
                      <div>Initial Reserve (OSLR): <span className="font-bold text-white">MWK {simResult.data.initialReserveAmount?.toLocaleString()}</span></div>
                      <div>Claim Status: <span className="font-bold text-white">{simResult.data.lodgedClaim.status}</span></div>
                      <div>Police Incident Filed: <span className="font-bold text-white">Yes ({simResult.data.lodgedClaim.policeStation})</span></div>
                    </div>
                  )}

                  {/* Takeover Summary */}
                  {simResult.data.takeoverTriggered !== undefined && (
                    <div className="bg-amber-950/30 border border-amber-900/50 p-4 rounded-xl text-amber-300 space-y-1 text-xs">
                      <div className="font-bold text-sm text-amber-200">✓ Live Human Agent Escalation Verified</div>
                      <div>Takeover Mode Active: <span className="font-bold text-white">True</span></div>
                      <div>Bot Auto-Reply Suppressed: <span className="font-bold text-white">True</span></div>
                      <div>Queued Messages for Agent: <span className="font-bold text-white">{simResult.data.unreadCountWhileTakeover} message(s)</span></div>
                      <div>Agent Response Dispatched: <span className="font-bold text-white">"{simResult.data.agentReplyDispatched}"</span></div>
                    </div>
                  )}

                  {/* Raw Payload Summary */}
                  {simResult.data.rawPayload && (
                    <div className="space-y-2">
                      <div className="text-[11px] text-slate-400 font-bold uppercase">Parsed Meta Inbound Message:</div>
                      <div className="p-3 bg-slate-900 rounded-xl text-teal-300 text-xs">
                        From: {simResult.data.parsedMessage?.fromPhoneNumber} | Type: {simResult.data.parsedMessage?.messageType} |
                        InteractiveId: {simResult.data.parsedMessage?.interactiveReplyId || 'None'} | Text: "{simResult.data.parsedMessage?.textBody}"
                      </div>
                      <div className="text-[11px] text-slate-400 font-bold uppercase">Raw Meta Webhook JSON Payload:</div>
                      <pre className="p-3 bg-slate-900 rounded-xl overflow-x-auto text-[11px] text-slate-300">
                        {simResult.data.rawPayload}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
